import { BifrostChunk, MAX_CHUNKS_PER_TRANSFER } from "../types/protocol";
import { utf8ByteLength } from "./ChunkSender";

export const DEFAULT_TRANSFER_TIMEOUT_MS = 30_000;
export const DEFAULT_MAX_TRANSFER_BYTES = 16 * 1024 * 1024;
const MAX_TRANSFER_LIFETIME_MS = 5 * 60_000;

function isValidBifrostChunk(chunk: any): chunk is BifrostChunk {
  return (
    chunk?.type === "bifrost_chunk" &&
    typeof chunk.transferId === "string" &&
    chunk.transferId.length > 0 &&
    (chunk.responseId === undefined ||
      (typeof chunk.responseId === "string" && chunk.responseId.length > 0)) &&
    typeof chunk.data === "string" &&
    chunk.data.length > 0 &&
    Number.isInteger(chunk.total) &&
    chunk.total > 0 &&
    chunk.total <= MAX_CHUNKS_PER_TRANSFER &&
    Number.isInteger(chunk.index) &&
    chunk.index >= 0 &&
    chunk.index < chunk.total
  );
}

/**
 * Why the assembler refused a chunk. Reported through `onReject` so that a drop
 * is never silent — an unexplained missing message is the failure mode this
 * bridge has historically been most expensive to diagnose.
 */
export type ChunkRejectReason =
  | "malformed"
  | "too-large"
  | "at-capacity"
  | "expired";

export interface ChunkAssemblerConfig {
  transferTimeoutMs?: number;
  maxConcurrentTransfers?: number;
  /** Cap on a single transfer's buffered payload. Mirrors ChunkSender.maxTransferSize. */
  maxTransferBytes?: number;
  /** Cap across all in-flight transfers, so concurrency cannot multiply the ceiling. */
  maxTotalBytes?: number;
  onReject?: (transferId: string, reason: ChunkRejectReason) => void;
  onAccept?: (chunk: BifrostChunk) => void;
}

interface PendingTransfer {
  chunks: Map<number, string>;
  total: number;
  responseId?: string;
  receivedCount: number;
  bytes: number;
  metadataBytes: number;
  createdAt: number;
  lastProgressAt: number;
  timeoutHandle?: ReturnType<typeof setTimeout>;
}

export class ChunkAssembler {
  private transfers = new Map<string, PendingTransfer>();
  private bufferedBytes = 0;
  private readonly transferTimeoutMs: number;
  private readonly maxConcurrentTransfers: number;
  private readonly maxTransferBytes: number;
  private readonly maxTotalBytes: number;
  private readonly onReject?: (
    transferId: string,
    reason: ChunkRejectReason,
  ) => void;
  private readonly onAccept?: (chunk: BifrostChunk) => void;

  constructor(config: ChunkAssemblerConfig = {}) {
    this.transferTimeoutMs =
      config.transferTimeoutMs ?? DEFAULT_TRANSFER_TIMEOUT_MS;
    this.maxConcurrentTransfers = config.maxConcurrentTransfers ?? 4;
    this.maxTransferBytes =
      config.maxTransferBytes ?? DEFAULT_MAX_TRANSFER_BYTES;
    // Headroom over one max-size transfer so a single large message does not
    // sit exactly on the ceiling and evict all concurrent small traffic.
    this.maxTotalBytes = config.maxTotalBytes ?? 32 * 1024 * 1024;
    this.onReject = config.onReject;
    this.onAccept = config.onAccept;
  }

  addChunk(chunk: BifrostChunk): string | null {
    const transferId =
      typeof chunk?.transferId === "string" ? chunk.transferId : "";

    // Validate before allocating: an envelope the peer got wrong must not cost a
    // transfer slot or a byte of buffer. `Number.isInteger` also rejects the
    // string-index variant ("0"), which would otherwise slip past the duplicate
    // guard below because Map keys by identity.
    if (!isValidBifrostChunk(chunk)) {
      this.onReject?.(transferId, "malformed");
      return null;
    }

    const { index, total, data } = chunk;

    let transfer = this.transfers.get(transferId);
    let dataBytes: number | undefined;

    if (
      transfer &&
      (transfer.total !== total || transfer.responseId !== chunk.responseId)
    ) {
      // The peer contradicted its own metadata mid-transfer. Nothing it has
      // sent for this transfer can be trusted, so drop the whole thing rather
      // than complete or extend whichever call happened to arrive first.
      this.discard(transferId);
      this.onReject?.(transferId, "malformed");
      return null;
    }

    if (!transfer) {
      if (this.transfers.size >= this.maxConcurrentTransfers) {
        this.onReject?.(transferId, "at-capacity");
        return null;
      }

      const metadataBytes =
        utf8ByteLength(transferId) +
        (chunk.responseId ? utf8ByteLength(chunk.responseId) : 0);
      dataBytes = utf8ByteLength(data);
      if (dataBytes > this.maxTransferBytes) {
        this.onReject?.(transferId, "too-large");
        return null;
      }
      if (metadataBytes + dataBytes > this.maxTotalBytes) {
        this.onReject?.(transferId, "at-capacity");
        return null;
      }
      if (this.bufferedBytes + metadataBytes > this.maxTotalBytes) {
        this.evictLargestUntil(this.maxTotalBytes - metadataBytes, transferId);
        if (this.bufferedBytes + metadataBytes > this.maxTotalBytes) {
          this.onReject?.(transferId, "at-capacity");
          return null;
        }
      }

      transfer = {
        chunks: new Map(),
        total,
        responseId: chunk.responseId,
        receivedCount: 0,
        bytes: 0,
        metadataBytes,
        createdAt: performance.now(),
        lastProgressAt: performance.now(),
      };
      this.transfers.set(transferId, transfer);
      this.bufferedBytes += metadataBytes;
    }

    if (transfer.chunks.has(index)) {
      if (transfer.chunks.get(index) !== data) {
        this.discard(transferId);
        this.onReject?.(transferId, "malformed");
      }
      return null; // An exact duplicate retransmit is benign.
    }

    if (dataBytes === undefined) {
      dataBytes = utf8ByteLength(data);
    }

    // Distinguish the two ceilings: exceeding maxTransferBytes means this
    // message is genuinely oversized, while exceeding maxTotalBytes means the
    // shared budget is exhausted by other traffic and says nothing about this
    // message's size.
    if (transfer.bytes + dataBytes > this.maxTransferBytes) {
      this.discard(transferId);
      this.onReject?.(transferId, "too-large");
      return null;
    }
    if (
      transfer.metadataBytes + transfer.bytes + dataBytes >
      this.maxTotalBytes
    ) {
      this.discard(transferId);
      this.onReject?.(transferId, "at-capacity");
      return null;
    }
    if (this.bufferedBytes + dataBytes > this.maxTotalBytes) {
      // Make room by dropping the biggest other transfer first. Discarding the
      // arriving one instead punishes whichever transfer happened to cross the
      // line and throws away its progress, while the transfer actually holding
      // the memory survives.
      this.evictLargestUntil(this.maxTotalBytes - dataBytes, transferId);

      if (this.bufferedBytes + dataBytes > this.maxTotalBytes) {
        this.discard(transferId);
        this.onReject?.(transferId, "at-capacity");
        return null;
      }
    }

    transfer.chunks.set(index, data);
    transfer.bytes += dataBytes;
    this.bufferedBytes += dataBytes;
    transfer.receivedCount++;
    transfer.lastProgressAt = performance.now();
    this.scheduleTimeout(transferId, transfer);

    // Indices are validated into 0..total-1 and de-duplicated, so a full count
    // means every slot is filled. assemble() therefore cannot hit a hole, which
    // join() would otherwise coerce to "" and emit as a truncated message.
    const assembled =
      transfer.receivedCount === transfer.total
        ? this.assemble(transferId, transfer)
        : null;

    // Acceptance observers may synchronously re-enter or throw. Complete every
    // state transition first and keep observer failures outside the transport.
    try {
      this.onAccept?.(chunk);
    } catch {
      // Observer failures cannot un-accept a validated chunk.
    }
    return assembled;
  }

  getPendingTransferCount(): number {
    return this.transfers.size;
  }

  /** Bytes currently held across all in-flight transfers. */
  getBufferedBytes(): number {
    return this.bufferedBytes;
  }

  destroy(): void {
    for (const [, transfer] of this.transfers) {
      if (transfer.timeoutHandle) {
        clearTimeout(transfer.timeoutHandle);
      }
    }
    this.transfers.clear();
    this.bufferedBytes = 0;
  }

  /** Drops the largest transfers, other than `keep`, until `budget` is free. */
  private evictLargestUntil(budget: number, keep: string): void {
    while (this.bufferedBytes > budget) {
      let victim: string | undefined;
      let victimBytes = -1;
      for (const [id, t] of this.transfers) {
        const retainedBytes = t.bytes + t.metadataBytes;
        if (id !== keep && retainedBytes > victimBytes) {
          victim = id;
          victimBytes = retainedBytes;
        }
      }
      if (victim === undefined) return;
      this.discard(victim);
      this.onReject?.(victim, "at-capacity");
    }
  }

  private discard(transferId: string): void {
    const transfer = this.transfers.get(transferId);
    if (!transfer) return;
    if (transfer.timeoutHandle) {
      clearTimeout(transfer.timeoutHandle);
    }
    this.bufferedBytes -= transfer.bytes + transfer.metadataBytes;
    this.transfers.delete(transferId);
  }

  private assemble(transferId: string, transfer: PendingTransfer): string {
    if (transfer.timeoutHandle) {
      clearTimeout(transfer.timeoutHandle);
    }

    const parts = new Array<string>(transfer.total);
    for (let i = 0; i < transfer.total; i++) {
      parts[i] = transfer.chunks.get(i) as string;
    }

    transfer.chunks.clear();
    this.bufferedBytes -= transfer.bytes + transfer.metadataBytes;
    this.transfers.delete(transferId);
    return parts.join("");
  }

  private scheduleTimeout(transferId: string, transfer: PendingTransfer): void {
    if (transfer.timeoutHandle) clearTimeout(transfer.timeoutHandle);
    const now = performance.now();
    const delay = Math.max(
      0,
      Math.min(
        transfer.lastProgressAt + this.transferTimeoutMs - now,
        transfer.createdAt + MAX_TRANSFER_LIFETIME_MS - now,
      ),
    );
    transfer.timeoutHandle = setTimeout(() => {
      if (this.transfers.get(transferId) !== transfer) return;
      const current = performance.now();
      if (
        current - transfer.lastProgressAt < this.transferTimeoutMs &&
        current - transfer.createdAt < MAX_TRANSFER_LIFETIME_MS
      ) {
        this.scheduleTimeout(transferId, transfer);
        return;
      }
      this.discard(transferId);
      this.onReject?.(transferId, "expired");
    }, delay);
  }
}
